import { createContext, useCallback, useContext, useMemo } from "react";
import { environmentService } from "@miplatform/api";
import { appStorage } from "@miplatform/utils";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";

const AppInsightsContext = createContext(null);

const onErrorComponent = () => <h1>Something went wrong.</h1>;

export function AppInsightsProvider({ children, reactPlugin, history }) {
  const runAppInsights = useCallback(() => {
    try {
      const appInsightsInstance = new ApplicationInsights({
        config: {
          connectionString: appStorage.getItem("connectionString"),
          extensions: [reactPlugin],
          extensionConfig: {
            [reactPlugin.identifier]: { history },
          },
        },
      });
      appInsightsInstance.loadAppInsights();
      appInsightsInstance.trackPageView();
      appInsightsInstance.addTelemetryInitializer((envelope) => {
        envelope.data.applicationName = "MiView";
        envelope.tags["ai.cloud.role"] = "MiView";
      });

      window.onunhandledrejection = (event) =>
        appInsightsInstance.trackException({ exception: event.reason.stack });
    } catch (e) {
      console.log(e);
    }
  }, [history, reactPlugin]);

  if (appStorage.getItem("connectionString")) {
    runAppInsights();
  }

  const appInsightProviderValue = useMemo(() => {
    const getAppInsightsConnectionString = async () => {
      const connectionString =
        await environmentService.logging.getConnectionString();
      if (!connectionString) {
        return;
      }
      appStorage.setItem("connectionString", connectionString);
      runAppInsights();
    };
    return { getAppInsightsConnectionString };
  }, [runAppInsights]);

  return (
    <AppInsightsContext.Provider value={appInsightProviderValue}>
      <AppInsightsErrorBoundary
        onError={onErrorComponent}
        appInsights={reactPlugin}
      >
        {children}
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  );
}

export const useAppInsights = () => {
  const appInsightsContext = useContext(AppInsightsContext);
  if (!appInsightsContext) {
    throw new Error("Application insights context not initialized!å");
  }
  return appInsightsContext;
};
